import { defineStore } from 'pinia';
import ISiteUrlSource from '@/interfaces/ISiteUrlSource';
import UrlSourceTypes from '@/enums/UrlSourceTypes';
import { urlToUrlSourceMap } from '@/utils/urlToUrlSourceMap';
import UrlTypes from '@/enums/UrlTypes';
import Provider from '@/enums/Provider';

export const useUrlSourcesStore = defineStore('urlsSource', {
  state: () => ({
    siteUrls: new Array<ISiteUrlSource>(),
  }),

  getters: {
    getNumberOfCategoryUrls(): number {
      return this.siteUrls.filter(
        (siteUrls) => siteUrls.urlType === UrlSourceTypes.CATEGORY
      ).length;
    },
    getNumberOfProductUrls(): number {
      return this.siteUrls.filter(
        (siteUrls) => siteUrls.urlType === UrlSourceTypes.PRODUCT
      ).length;
    },
    getNumberOfPageUrls(): number {
      return this.siteUrls.filter(
        (siteUrls) => siteUrls.urlType === UrlSourceTypes.PAGE
      ).length;
    },
    // Update getter to determine provider based on URL
    getHomepageProvider(): Provider | undefined {
      const homepage = this.siteUrls.find(
        (siteUrl) => siteUrl.urlType === UrlSourceTypes.HOME_PAGE
      );
      if (homepage) {
        if (homepage.urlSource.includes('kbsystems')) {
          return Provider.KBS;
        } else if (homepage.urlSource.includes('inflatableoffice')) {
          return Provider.IO;
        } else if (homepage.urlSource.includes('eventrentalsystems')) {
          return Provider.ERS;
        }
        return Provider.OTHER;
      }
      return undefined;
    },
    getHomepageSource(): string | undefined {
      const homepage = this.siteUrls.find(
        (siteUrl) => siteUrl.urlType === UrlSourceTypes.HOME_PAGE
      );
      if (homepage) {
        return homepage.urlSource;
      }
    },
    getSitemapUrlSource(): string | undefined {
      const sitemap = this.siteUrls.find(
        (siteUrl) => siteUrl.urlType === UrlSourceTypes.SITEMAP
      );
      if (sitemap) {
        return sitemap.urlSource;
      }
    },
    getCategoriesSource(): ISiteUrlSource[] {
      return this.siteUrls.filter(
        (siteUrl) => siteUrl.urlType === UrlSourceTypes.CATEGORY
      );
    },
    getProductsSource(): ISiteUrlSource[] {
      return this.siteUrls.filter(
        (siteUrl) => siteUrl.urlType === UrlSourceTypes.PRODUCT
      );
    },
    getPagesSource(): ISiteUrlSource[] {
      return this.siteUrls.filter(
        (siteUrl) => siteUrl.urlType === UrlSourceTypes.PAGE
      );
    },
    getCategoryHubSources(): ISiteUrlSource[] {
      return this.siteUrls.filter(
        (siteUrl) => siteUrl.urlType === UrlSourceTypes.CATEGORY_HUB
      );
    },
    getJoinedCategoryHubSources(): string {
      return this.siteUrls
        .filter(u => u.urlType === UrlSourceTypes.CATEGORY_HUB)
        .map(u => u.urlSource)
        .join(' ');
    },
  },
  actions: {
    addSiteSource(
      pathname: string,
      urlSource: string,
      urlType: UrlSourceTypes
    ) {
      const newUrlSource: ISiteUrlSource = {
        pathname,
        urlSource,
        urlType: urlType,
      };
      this.siteUrls.push(newUrlSource);
    },
    clearUrlSourceStore() {
      this.siteUrls = new Array<ISiteUrlSource>();
    },
    addHomepage(urlSource: string) {
      this.addSiteSource('/', urlSource, UrlSourceTypes.HOME_PAGE);
    },
    addSitemap(urlSource: string) {
      this.addSiteSource('/sitemap.xml', urlSource, UrlSourceTypes.SITEMAP);
    },
    addCategory(pathname: string, urlSource: string) {
      this.addSiteSource(pathname, urlSource, UrlSourceTypes.CATEGORY);
    },
    addProduct(pathname: string, urlSource: string) {
      this.addSiteSource(pathname, urlSource, UrlSourceTypes.PRODUCT);
    },
    addPage(pathname: string, urlSource: string) {
      this.addSiteSource(pathname, urlSource, UrlSourceTypes.PAGE);
    },
    addCategoryHub(pathname: string, urlSource: string) {
      this.addSiteSource(pathname, urlSource, UrlSourceTypes.CATEGORY_HUB);
    },
    addMappedSiteSourceFromPageSourceHtml(
      pathname: string,
      pageSourceHtml: string,
      actualType: UrlTypes
    ) {
      const sourceType = urlToUrlSourceMap[actualType];
      this.addSiteSource(pathname, pageSourceHtml, sourceType);
    },
    hasUrl(pathname: string) {
      return this.siteUrls.some((url) => url.pathname === pathname);
    },
    getAllPathnamesByType(urlType: UrlSourceTypes): string[] {
      return this.siteUrls
        .filter((siteUrl) => siteUrl.urlType === urlType)
        .map((siteUrl) => siteUrl.pathname);
    },
    getSourceByPathname(pathname: string): string | undefined {
      return this.siteUrls.find((siteUrl) => siteUrl.pathname === pathname)?.urlSource;
    }
  },
});
